import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit'
import * as dashboardsApi from '@/api/modules/dashboards'
import { normalizeData } from '@/utils/jsonapi'
import { getApiErrorMessage } from '@/utils/apiErrors'

// Create entity adapter for dashboards
const dashboardsAdapter = createEntityAdapter({
  selectId: (dashboard) => dashboard.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
})

/**
 * Async thunk to fetch a single dashboard by ID
 * API already returns normalized data from JSON:API response
 */
export const fetchDashboard = createAsyncThunk(
  'dashboards/fetchDashboard',
  async (dashboardId, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dashboardsApi.fetchDashboard(dashboardId)
      // Normalize in slice (API now returns raw JSON:API)
      const normalized = normalizeData(jsonApiResponse.data)

      // Resolve widget relationships from included resources
      if (jsonApiResponse.included) {
        const widgetRelationships = jsonApiResponse.data.relationships?.widgets?.data || []
        normalized.widgets = widgetRelationships.map(ref => {
          const fullWidget = jsonApiResponse.included.find(item => item.type === 'widget' && item.id === ref.id)
          if (fullWidget) {
            const widget = normalizeData(fullWidget)
            // Resolve metrics for this widget
            const metricRelationships = fullWidget.relationships?.metrics?.data || []
            widget.metrics = metricRelationships.map(metricRef => {
              const fullMetric = jsonApiResponse.included.find(item => item.type === 'metric' && item.id === metricRef.id)
              return fullMetric ? normalizeData(fullMetric) : { id: metricRef.id, type: metricRef.type }
            })
            return widget
          }
          return { id: ref.id, type: ref.type }
        })
      }

      return normalized
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to fetch dashboard')
      // The status rides on action.meta so the page can tell "not found"
      // from "failed" (E15) without changing the payload's string shape.
      return rejectWithValue(errorMessage, { status: error?.apiError?.status ?? null })
    }
  }
)

/**
 * Async thunk to fetch all dashboards
 * API already returns normalized data from JSON:API response
 */
export const fetchDashboards = createAsyncThunk(
  'dashboards/fetchDashboards',
  async (options = {}, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dashboardsApi.fetchDashboards(options)
      // Normalize JSON:API response in slice for consistency
      return {
        data: normalizeData(jsonApiResponse.data || []),
        meta: jsonApiResponse.meta,
        included: jsonApiResponse.included
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to fetch dashboards')
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to create a new dashboard
 * API already returns normalized data from JSON:API response
 */
export const createDashboard = createAsyncThunk(
  'dashboards/createDashboard',
  async (dashboardData, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dashboardsApi.createDashboard(dashboardData)
      // Normalize in slice for consistency
      return normalizeData(jsonApiResponse.data)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to create dashboard')
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to update an existing dashboard
 * API already returns normalized data from JSON:API response
 */
export const updateDashboard = createAsyncThunk(
  'dashboards/updateDashboard',
  async ({ dashboardId, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dashboardsApi.updateDashboard(dashboardId, data)
      // Normalize in slice for consistency
      return normalizeData(jsonApiResponse.data)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to update dashboard')
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to delete a dashboard
 */
export const deleteDashboard = createAsyncThunk(
  'dashboards/deleteDashboard',
  async (dashboardId, { rejectWithValue }) => {
    try {
      await dashboardsApi.deleteDashboard(dashboardId)
      return dashboardId
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to delete dashboard')
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to clone a dashboard
 * @param {Object} params
 * @param {string} params.dashboardId - Dashboard UUID to clone
 * @param {string} [params.name] - Optional name for the cloned dashboard
 */
export const cloneDashboard = createAsyncThunk(
  'dashboards/cloneDashboard',
  async ({ dashboardId, name }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dashboardsApi.cloneDashboard(dashboardId, name)
      return normalizeData(jsonApiResponse.data)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to clone dashboard')
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to update widget positions
 * API already returns normalized dashboard with updated widget positions
 */
export const updateWidgetPositions = createAsyncThunk(
  'dashboards/updateWidgetPositions',
  async ({ dashboardId, widgets }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dashboardsApi.updateWidgetPositions(dashboardId, widgets)
      // Normalize in slice (API now returns raw JSON:API)
      const normalized = normalizeData(jsonApiResponse.data)

      // Resolve widget relationships from included resources
      if (jsonApiResponse.included) {
        const widgetRelationships = jsonApiResponse.data.relationships?.widgets?.data || []
        normalized.widgets = widgetRelationships.map(ref => {
          const fullWidget = jsonApiResponse.included.find(item => item.type === 'widget' && item.id === ref.id)
          if (fullWidget) {
            const widget = normalizeData(fullWidget)
            // Resolve metrics for this widget
            const metricRelationships = fullWidget.relationships?.metrics?.data || []
            widget.metrics = metricRelationships.map(metricRef => {
              const fullMetric = jsonApiResponse.included.find(item => item.type === 'metric' && item.id === metricRef.id)
              return fullMetric ? normalizeData(fullMetric) : { id: metricRef.id, type: metricRef.type }
            })
            return widget
          }
          return { id: ref.id, type: ref.type }
        })
      }

      return normalized
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.message || 'Failed to update widget positions'
      )
    }
  }
)

/**
 * Dashboard Slice
 */
const dashboardsSlice = createSlice({
  name: 'dashboards',
  initialState: dashboardsAdapter.getInitialState({
    currentDashboard: null,
    loading: false,
    error: null,
    errorStatus: null,
    // The dashboard the stored `error` belongs to (E15 review): a 404 for one
    // dashboard must never show on another.
    errorDashboardId: null,
    pagination: null, // Store pagination metadata
    included: null, // Store included resources for relationships
  }),
  reducers: {
    clearCurrentDashboard: (state) => {
      state.currentDashboard = null
      state.error = null
    },
    /**
     * A dashboard page is (re)opened for `action.payload` (its id). Dispatched
     * before that page's load decides whether to fetch, so a stored error
     * never survives into another dashboard, and a stored error for this one
     * always forces a fetch: the current dashboard is dropped, so no "is it
     * already loaded?" check (the page-mount freshness reuse) can skip it.
     */
    dashboardOpened: (state, action) => {
      if (!state.error) return
      if (String(state.errorDashboardId) === String(action.payload)) {
        state.currentDashboard = null
        return
      }
      state.error = null
      state.errorStatus = null
      state.errorDashboardId = null
    },
    clearError: (state) => {
      state.error = null
    },
    updateLocalWidgetPositions: (state, action) => {
      // Update widget positions locally without API call
      if (state.currentDashboard) {
        state.currentDashboard.widgets = action.payload
      }
    },
    upsertMany: (state, action) => {
      dashboardsAdapter.upsertMany(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    // Fetch Dashboard
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.loading = true
        state.error = null
        state.errorStatus = null
        state.errorDashboardId = null
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.loading = false
        state.currentDashboard = action.payload
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.errorStatus = action.meta?.status ?? null
        state.errorDashboardId = action.meta?.arg ?? null
      })

    // Fetch Dashboards
    builder
      .addCase(fetchDashboards.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        state.loading = false
        dashboardsAdapter.setAll(state, action.payload?.data || [])
        // Store pagination metadata
        state.pagination = action.payload?.meta?.pagination || null
        // Store included resources for relationship lookups
        state.included = action.payload?.included || null
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Create Dashboard
    builder
      .addCase(createDashboard.fulfilled, (state, action) => {
        dashboardsAdapter.addOne(state, action.payload)
      })

    // Update Dashboard
    builder
      .addCase(updateDashboard.fulfilled, (state, action) => {
        dashboardsAdapter.upsertOne(state, action.payload)
        if (state.currentDashboard?.id === action.payload.id) {
          // A settings PATCH answers with widget references only (no
          // `included`), while the open dashboard holds the widgets
          // fetchDashboard resolved in full. Keep those, or saving the name
          // or filters from the dashboard page would strip every widget.
          state.currentDashboard = {
            ...action.payload,
            widgets: state.currentDashboard.widgets ?? action.payload.widgets,
          }
        }
      })

    // Clone Dashboard
    builder
      .addCase(cloneDashboard.fulfilled, (state, action) => {
        dashboardsAdapter.addOne(state, action.payload)
      })

    // Delete Dashboard
    builder
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        dashboardsAdapter.removeOne(state, action.payload)
        if (state.currentDashboard?.id === action.payload) {
          state.currentDashboard = null
        }
      })

    // Update Widget Positions
    builder
      .addCase(updateWidgetPositions.fulfilled, (state, action) => {
        if (state.currentDashboard?.id === action.payload.id) {
          state.currentDashboard = action.payload
        }
      })
  },
})

export const { clearCurrentDashboard, clearError, dashboardOpened, updateLocalWidgetPositions, upsertMany } =
  dashboardsSlice.actions

/** Statuses that mean the dashboard isn't there for this user, not that loading failed (E15). */
const NOT_FOUND_STATUSES = [403, 404]

/**
 * The stored load error, only when it belongs to dashboard `dashboardId`.
 *
 * @param {Object} state
 * @param {string} dashboardId
 * @returns {string|null}
 */
export const selectDashboardLoadError = (state, dashboardId) => {
  const { error, errorDashboardId } = state.dashboards || {}
  return error && String(errorDashboardId) === String(dashboardId) ? error : null
}

/**
 * Whether dashboard `dashboardId` failed to load because it isn't there for
 * this user (403/404) rather than because loading failed.
 *
 * @param {Object} state
 * @param {string} dashboardId
 * @returns {boolean}
 */
export const selectIsDashboardNotFound = (state, dashboardId) =>
  Boolean(selectDashboardLoadError(state, dashboardId)) && NOT_FOUND_STATUSES.includes(state.dashboards?.errorStatus)

// Entity adapter selectors
export const {
  selectAll: selectAllDashboards,
  selectById: selectDashboardById,
} = dashboardsAdapter.getSelectors((state) => state.dashboards || dashboardsAdapter.getInitialState())

// Legacy selector for backward compatibility
export const selectDashboards = selectAllDashboards

// Other selectors
export const selectCurrentDashboard = (state) => state.dashboards?.currentDashboard || null
export const selectLoading = (state) => state.dashboards?.loading || false
export const selectError = (state) => state.dashboards?.error || null

export default dashboardsSlice.reducer
